import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import {
  Container, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, TextField, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Alert,
  Menu, MenuItem, IconButton, Box, Stack, TableSortLabel, InputAdornment, CircularProgress, Grid, Card, CardContent,
  Snackbar, LinearProgress, Chip, Collapse
} from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PeopleIcon from '@mui/icons-material/People';
import LockIcon from '@mui/icons-material/Lock';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import GoogleIcon from '@mui/icons-material/Google';
import PersonIcon from '@mui/icons-material/Person';
import AuditLogsTable from '../components/admin/AuditLogsTable';
import InfoIcon from '@mui/icons-material/Info';
import StorageIcon from '@mui/icons-material/Storage';
import MemoryIcon from '@mui/icons-material/Memory';
import DescriptionIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SpeedIcon from '@mui/icons-material/Speed';
import ErrorIcon from '@mui/icons-material/Error';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import AppsIcon from '@mui/icons-material/Apps';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import PermMediaOutlinedIcon from '@mui/icons-material/PermMediaOutlined';

interface User {
  id: number;
  username: string;
  email: string;
  creationDate: string;
  expirationDate: string;
  lastLoginAt: string;
  provider: string;
  admin: boolean;
  accountNeverExpires: boolean;
}

interface OnlineUser {
  id: number;
  username: string;
  email: string;
  loginTime: string;
  provider: string;
}

interface UserDetails {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
  expirationDate: string | null;
  lastLoginAt: string | null;
  admin: boolean;
  documentCount: number;
  enabled?: boolean;
  roles?: string[];
  provider?: string;
  creationDate?: string;
  accountNeverExpires?: boolean;
}

type Order = 'asc' | 'desc';
type SortField = 'username' | 'creationDate' | 'expirationDate' | 'lastLoginAt';

interface SortConfig {
  field: SortField;
  order: Order;
}

interface UserStats {
  totalUsers: number;
  activeUsers: number;
  expiredUsers: number;
  adminUsers: number;
  regularUsers: number;
  usersByProvider: {
    LOCAL: number;
    GOOGLE: number;
  };
  redisDbSize: number;
  redisMemoryUsage: number;
  totalDocuments: number;
  totalRevisions?: number;
  totalRevisionBytes?: number;
  totalMediaFiles?: number;
  totalMediaBytes?: number;
  mediaStagingBytes?: number;
  mediaByContentBytes?: number;
  postgresDatabaseBytes?: number;
  postgresDocumentRevisionsRelationBytes?: number;
  mediaVolumeTotalBytes?: number;
  mediaVolumeUsableBytes?: number;
  mediaVolumeUsedBytes?: number;
  mediaStagingRetentionDays?: number;
  mediaStagingPurgeEnabled?: boolean;
  newDocuments24h: number;
  newDocuments7d: number;
  newDocuments30d: number;
  newUsers24h: number;
  newUsers7d: number;
  newUsers30d: number;
  activeSessions: number;
  averageSessionDuration: number;
}

interface SystemHealth {
  cpuUsage: number;
  memoryUsage: number;
  jvmMemoryUsage: number;
  threadCount: number;
  databaseStatus: 'healthy' | 'degraded' | 'unhealthy';
  redisStatus: 'healthy' | 'degraded' | 'unhealthy';
  embeddingStatus: 'healthy' | 'degraded' | 'unhealthy';
  apiResponseTime: number;
  embeddingError?: string;
}

interface OpenDocument {
  id: string;
  name: string;
  user: {
    id: number;
    username: string;
    email: string;
  };
  creationDate: string;
  updatedAt: string;
}

interface UserDocument {
  id: string;
  name: string;
  creationDate: string;
  updatedAt: string;
  revisionCount?: number;
  latestRevisionAt?: string;
}

interface DocumentRevisionRow {
  id: number;
  createdAt: any;
  documentRevision: number;
  sizeBytes: number;
}

const COLORS = ['#1976d2', '#db4437'];

function clampStoragePercent(numerator: number, denominator: number): number {
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator <= 0) return 0;
  return Math.min(100, Math.max(0, (numerator / denominator) * 100));
}

const AdminConsolePage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [openDocuments, setOpenDocuments] = useState<OpenDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editUser, setEditUser] = useState<User | null>(null);
  const [newExpiration, setNewExpiration] = useState('');
  const [deleteUser, setDeleteUser] = useState<User | null>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [stagingPurgeDialogOpen, setStagingPurgeDialogOpen] = useState(false);
  const [stagingPurgeLoading, setStagingPurgeLoading] = useState(false);
  const [stagingPurgeRetentionDaysInput, setStagingPurgeRetentionDaysInput] = useState('7');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'username', order: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [openDocumentsSearch, setOpenDocumentsSearch] = useState('');
  const [reloadCacheDoc, setReloadCacheDoc] = useState<OpenDocument | null>(null);
  const [reloadCacheLoading, setReloadCacheLoading] = useState(false);
  const [saveToDbDoc, setSaveToDbDoc] = useState<OpenDocument | null>(null);
  const [saveToDbLoading, setSaveToDbLoading] = useState(false);
  const [viewDetailsUser, setViewDetailsUser] = useState<UserDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [sanityReport, setSanityReport] = useState<any>(null);
  const [loadingSanity, setLoadingSanity] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    systemHealth: true,
    statistics: true,
    userManagement: true,
    auditLogs: true,
    openDocuments: true,
    sanityChecks: true,
  });
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [userDocuments, setUserDocuments] = useState<UserDocument[]>([]);
  const [viewDocumentsUser, setViewDocumentsUser] = useState<User | null>(null);
  const [deleteDocument, setDeleteDocument] = useState<UserDocument | null>(null);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [viewRevisionsDocument, setViewRevisionsDocument] = useState<UserDocument | null>(null);
  const [documentRevisions, setDocumentRevisions] = useState<DocumentRevisionRow[]>([]);
  const [loadingRevisions, setLoadingRevisions] = useState(false);
  const [documentActionsAnchorEl, setDocumentActionsAnchorEl] = useState<null | HTMLElement>(null);
  const [documentActionsDoc, setDocumentActionsDoc] = useState<UserDocument | null>(null);
  const [repairingId, setRepairingId] = useState<string | null>(null);
  const [repairingAll, setRepairingAll] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/users');
      console.log('User data:', res.data);
      setUsers(res.data);
    } catch (err) {
      setError('Failed to fetch users.');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/statistics');
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch statistics:', err);
    }
  };

  useEffect(() => {
    if (!stagingPurgeDialogOpen || !stats) return;
    const d =
      stats.mediaStagingRetentionDays != null && stats.mediaStagingRetentionDays > 0
        ? stats.mediaStagingRetentionDays
        : 7;
    setStagingPurgeRetentionDaysInput(String(d));
  }, [stagingPurgeDialogOpen, stats]);

  const confirmStagingPurge = async () => {
    setStagingPurgeLoading(true);
    setError('');
    try {
      const parsed = parseInt(stagingPurgeRetentionDaysInput.trim(), 10);
      const retentionDays = Number.isFinite(parsed) ? Math.min(3650, Math.max(1, parsed)) : 7;
      const res = await api.post<{
        purgedStagingObjects?: number;
        retentionDaysUsed?: number;
        retentionOverridden?: boolean;
      }>('/admin/content-media/purge-staging', null, { params: { retentionDays } });
      const n = res.data?.purgedStagingObjects ?? 0;
      const d = res.data?.retentionDaysUsed ?? retentionDays;
      setSuccess(`Removed ${n} stale staging object(s) (last modified older than ${d} day(s)).`);
      setStagingPurgeDialogOpen(false);
      await fetchStats();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: unknown } };
      const data = ax?.response?.data;
      const msg =
        typeof data === 'string'
          ? data
          : 'Failed to purge staging.';
      setError(msg);
    } finally {
      setStagingPurgeLoading(false);
    }
  };

  const fetchOnlineUsers = async () => {
    try {
      const response = await api.get('/admin/online-users');
      setOnlineUsers(response.data);
    } catch (err) {
      console.error('Failed to fetch online users:', err);
    }
  };

  const fetchSystemHealth = async () => {
    try {
      const startTime = performance.now();
      const response = await api.get('/admin/system-health');
      const endTime = performance.now();
      const responseTime = endTime - startTime;
      
      setSystemHealth({
        ...response.data,
        apiResponseTime: responseTime
      });
    } catch (err) {
      console.error('Failed to fetch system health:', err);
    }
  };

  const fetchOpenDocuments = async () => {
    try {
      const response = await api.get('/admin/open-documents');
      setOpenDocuments(response.data);
    } catch (err) {
      console.error('Failed to fetch open documents:', err);
    }
  };

  const fetchSanityReport = async () => {
    setLoadingSanity(true);
    try {
      const response = await api.get('/admin/sanity-checks');
      setSanityReport(response.data);
    } catch (err) {
      setSanityReport(null);
    } finally {
      setLoadingSanity(false);
    }
  };

  const runSanityChecks = async () => {
    setLoadingSanity(true);
    try {
      const response = await api.post('/admin/sanity-checks/run');
      setSanityReport(response.data);
    } catch (err) {
      setSanityReport(null);
    } finally {
      setLoadingSanity(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchStats();
    fetchOnlineUsers();
    fetchSystemHealth();
    fetchOpenDocuments();
    fetchSanityReport();
    const interval = setInterval(() => {
      fetchStats();
      fetchOnlineUsers();
      fetchSystemHealth();
      fetchOpenDocuments();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleSetExpiration = async () => {
    if (!editUser) return;
    setError(''); setSuccess('');
    try {
      await api.post('/admin/set-expiration', null, {
        params: { 
          username: editUser.username, 
          expirationDate: newExpiration || null 
        }
      });
      setSuccess('Expiration updated.');
      setEditUser(null);
      setNewExpiration('');
      fetchUsers();
    } catch (err: any) {
      setError(err?.response?.data || 'Failed to update expiration.');
    }
  };

  const handleToggleExpiration = async (user: User) => {
    setError(''); setSuccess('');
    try {
      await api.post(`/admin/user/${user.username}/toggle-expiration`);
      setSuccess('Account expiration status updated.');
      fetchUsers();
    } catch (err: any) {
      setError(err?.response?.data || 'Failed to update account expiration status.');
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUser) return;
    setError(''); setSuccess('');
    try {
      await api.delete(`/admin/user/${deleteUser.username}`);
      setSuccess('User deleted.');
      setDeleteUser(null);
      fetchUsers();
    } catch (err: any) {
      setError(err?.response?.data || 'Failed to delete user.');
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUser) return;
    setError(''); setSuccess('');
    try {
      await api.post(`/admin/user/${resetPasswordUser.username}/reset-password`);
      setSuccess(`Password reset email sent to ${resetPasswordUser.username}`);
      setResetPasswordUser(null);
    } catch (err: any) {
      setError(err?.response?.data || 'Failed to send password reset email.');
    }
  };

  const isCurrentUser = (user: User) => {
    return currentUser && user.username === currentUser.username;
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setSelectedUser(user);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedUser(null);
  };

  const fetchUserDetails = async (username: string) => {
    setLoadingDetails(true);
    setDetailsError(null);
    try {
      const response = await api.get(`/admin/user/${username}/details`);
      console.log('User details response:', response.data);
      setViewDetailsUser(response.data);
    } catch (err: any) {
      console.error('Error fetching user details:', err);
      const errorMessage = err?.response?.data?.message || 'Failed to fetch user details.';
      setDetailsError(errorMessage);
      setViewDetailsUser(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleAction = (action: 'expiration' | 'reset' | 'delete' | 'disable-expiration' | 'set-never-expire' | 'view-details' | 'make-admin' | 'remove-admin' | 'view-documents') => {
    if (!selectedUser) return;
    
    switch (action) {
      case 'expiration':
        setEditUser(selectedUser);
        setNewExpiration(selectedUser.expirationDate ? dayjs(selectedUser.expirationDate).format('YYYY-MM-DDTHH:mm') : '');
        break;
      case 'reset':
        setResetPasswordUser(selectedUser);
        break;
      case 'delete':
        setDeleteUser(selectedUser);
        break;
      case 'disable-expiration':
        handleDisableExpiration(selectedUser);
        break;
      case 'set-never-expire':
        handleSetNeverExpire(selectedUser);
        break;
      case 'view-details':
        fetchUserDetails(selectedUser.username);
        break;
      case 'make-admin':
        handleMakeAdmin(selectedUser.id);
        break;
      case 'remove-admin':
        handleRemoveAdmin(selectedUser.id);
        break;
      case 'view-documents':
        handleViewDocuments(selectedUser);
        break;
    }
    handleMenuClose();
  };

  const handleDisableExpiration = async (user: User) => {
    setError(''); setSuccess('');
    try {
      await api.post(`/admin/user/${user.username}/disable-expiration`);
      setSuccess('Account expiration enabled.');
      fetchUsers();
    } catch (err: any) {
      setError(err?.response?.data || 'Failed to enable account expiration.');
    }
  };

  const handleSetNeverExpire = async (user: User) => {
    setError(''); setSuccess('');
    try {
      await api.post(`/admin/user/${user.username}/set-never-expire`);
      setSuccess('Account set to never expire.');
      fetchUsers();
    } catch (err: any) {
      setError(err?.response?.data || 'Failed to set account to never expire.');
    }
  };

  const handleChangePassword = async () => {
    setError(''); setSuccess('');
    try {
      await api.post('/admin/change-password', null, {
        params: {
          currentPassword,
          newPassword
        }
      });
      setSuccess('Password changed successfully.');
      setChangePasswordOpen(false);
      setCurrentPassword('');
      setNewPassword('');
    } catch (err: any) {
      setError(err?.response?.data || 'Failed to change password.');
    }
  };

  const handleSort = (field: SortField) => {
    setSortConfig(prevConfig => ({
      field,
      order: prevConfig.field === field && prevConfig.order === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortedUsers = (users: User[]) => {
    return [...users].sort((a, b) => {
      const order = sortConfig.order === 'asc' ? 1 : -1;
      
      switch (sortConfig.field) {
        case 'username':
          return order * a.username.localeCompare(b.username);
        case 'creationDate':
          return order * (dayjs(a.creationDate).unix() - dayjs(b.creationDate).unix());
        case 'expirationDate':
          // Handle "Never" cases
          if (a.accountNeverExpires && !b.accountNeverExpires) return -1;
          if (!a.accountNeverExpires && b.accountNeverExpires) return 1;
          if (a.accountNeverExpires && b.accountNeverExpires) return 0;
          
          // Handle null expiration dates
          if (!a.expirationDate && !b.expirationDate) return 0;
          if (!a.expirationDate) return 1;
          if (!b.expirationDate) return -1;
          
          return order * (dayjs(a.expirationDate).unix() - dayjs(b.expirationDate).unix());
        case 'lastLoginAt':
          // Handle "Never" cases (null lastLoginAt)
          if (!a.lastLoginAt && !b.lastLoginAt) return 0;
          if (!a.lastLoginAt) return 1;
          if (!b.lastLoginAt) return -1;
          
          return order * (dayjs(a.lastLoginAt).unix() - dayjs(b.lastLoginAt).unix());
        default:
          return 0;
      }
    });
  };

  const isUserOnline = (username: string) => {
    return onlineUsers.some(user => user.username === username);
  };

  const renderSortLabel = (field: SortField, label: string) => (
    <TableSortLabel
      active={sortConfig.field === field}
      direction={sortConfig.field === field ? sortConfig.order : 'asc'}
      onClick={() => handleSort(field)}
    >
      {label}
    </TableSortLabel>
  );

  const filterUsers = (users: User[], searchTerm: string) => {
    if (!searchTerm) return users;
    const searchLower = searchTerm.toLowerCase();
    return users.filter(user => 
      user.username.toLowerCase().includes(searchLower)
    );
  };

  const filteredOpenDocuments = useMemo(() => {
    const q = openDocumentsSearch.trim().toLowerCase();
    if (!q) return openDocuments;
    return openDocuments.filter(doc => {
      const idStr = String(doc.id ?? '');
      return (
        doc.name.toLowerCase().includes(q) ||
        doc.user.username.toLowerCase().includes(q) ||
        (doc.user.email?.toLowerCase().includes(q) ?? false) ||
        idStr.toLowerCase().includes(q)
      );
    });
  }, [openDocuments, openDocumentsSearch]);

  const getProviderData = () => {
    if (!stats?.usersByProvider) return [];
    const data = [
      { name: 'Local', value: stats.usersByProvider.LOCAL || 0 },
      { name: 'Google', value: stats.usersByProvider.GOOGLE || 0 },
    ];
    return data;
  };

  const handleMakeAdmin = async (userId: number) => {
    setLoading(true);
    try {
      await api.post(`/admin/users/${userId}/admin`);
      // Refresh the users list
      await fetchUsers();
      // Show success message
      setSnackbar({
        open: true,
        message: 'User has been made an admin',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error making user admin:', error);
      // Don't show error message if the operation succeeded
      await fetchUsers();
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAdmin = async (userId: number) => {
    setLoading(true);
    try {
      await api.post(`/admin/users/${userId}/admin`);
      // Refresh the users list
      await fetchUsers();
      // Show success message
      setSnackbar({
        open: true,
        message: 'Admin privileges have been removed',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error removing admin privileges:', error);
      // Don't show error message if the operation succeeded
      await fetchUsers();
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getHealthColor = (value: number, thresholds: { warning: number; error: number }) => {
    if (value >= thresholds.error) return 'error';
    if (value >= thresholds.warning) return 'warning';
    return 'success';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'success';
      case 'degraded': return 'warning';
      case 'unhealthy': return 'error';
      default: return 'error';
    }
  };

  const handleCloseDocument = async (id: string) => {
    try {
      await api.post(`/admin/open-documents/${String(id)}/close`);
      setSnackbar({
        open: true,
        message: 'Document closed successfully',
        severity: 'success'
      });
      fetchOpenDocuments(); // Refresh the list
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to close document',
        severity: 'error'
      });
    }
  };

  const confirmReloadOpenDocumentCache = async () => {
    if (!reloadCacheDoc) return;
    setReloadCacheLoading(true);
    try {
      await api.post(`/admin/open-documents/${String(reloadCacheDoc.id)}/reload-from-database`);
      setSnackbar({
        open: true,
        message: 'Document cache reloaded from database',
        severity: 'success'
      });
      setReloadCacheDoc(null);
      fetchOpenDocuments();
    } catch (err: any) {
      const data = err.response?.data;
      const msg = typeof data === 'string' ? data : (data?.message ?? 'Failed to reload document cache');
      const status = err.response?.status;
      setSnackbar({
        open: true,
        message: status === 409 ? `${msg} Try again in a few seconds.` : msg,
        severity: 'error'
      });
    } finally {
      setReloadCacheLoading(false);
    }
  };

  const confirmPersistOpenDocumentToDatabase = async () => {
    if (!saveToDbDoc) return;
    setSaveToDbLoading(true);
    try {
      const response = await api.post(
        `/admin/open-documents/${String(saveToDbDoc.id)}/persist-to-database`
      );
      const msg =
        response.data?.message ??
        (response.data?.persisted ? 'Saved to database.' : 'Already up to date.');
      setSnackbar({
        open: true,
        message: msg,
        severity: 'success'
      });
      setSaveToDbDoc(null);
      fetchOpenDocuments();
    } catch (err: any) {
      const data = err.response?.data;
      const msg =
        typeof data?.message === 'string'
          ? data.message
          : typeof data === 'string'
            ? data
            : 'Failed to save document to database';
      const status = err.response?.status;
      setSnackbar({
        open: true,
        message: status === 409 ? `${msg} Try again in a few seconds.` : msg,
        severity: 'error'
      });
    } finally {
      setSaveToDbLoading(false);
    }
  };

  const handleViewDocuments = async (user: User) => {
    setViewDocumentsUser(user);
    setLoadingDocuments(true);
    try {
      const response = await api.get(`/admin/users/${user.id}/documents`);
      // Convert date arrays to proper date strings
      const formattedDocuments = response.data.map((doc: any) => ({
        ...doc,
        creationDate: Array.isArray(doc.creationDate) ? 
          dayjs(new Date(doc.creationDate[0], doc.creationDate[1] - 1, doc.creationDate[2], 
                        doc.creationDate[3], doc.creationDate[4], doc.creationDate[5])).format('YYYY-MM-DD HH:mm:ss') : 
          doc.creationDate,
        updatedAt: Array.isArray(doc.updatedAt) ? 
          dayjs(new Date(doc.updatedAt[0], doc.updatedAt[1] - 1, doc.updatedAt[2], 
                        doc.updatedAt[3], doc.updatedAt[4], doc.updatedAt[5])).format('YYYY-MM-DD HH:mm:ss') : 
          doc.updatedAt,
        latestRevisionAt: Array.isArray(doc.latestRevisionAt) ?
          dayjs(new Date(doc.latestRevisionAt[0], doc.latestRevisionAt[1] - 1, doc.latestRevisionAt[2],
                        doc.latestRevisionAt[3], doc.latestRevisionAt[4], doc.latestRevisionAt[5])).format('YYYY-MM-DD HH:mm:ss') :
          doc.latestRevisionAt,
        revisionCount: doc.revisionCount ?? 0
      }));
      setUserDocuments(formattedDocuments);
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to fetch user documents',
        severity: 'error'
      });
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleViewRevisions = async (doc: UserDocument) => {
    if (!viewDocumentsUser) return;
    setViewRevisionsDocument(doc);
    setLoadingRevisions(true);
    setDocumentRevisions([]);
    try {
      const response = await api.get(`/admin/users/${viewDocumentsUser.id}/documents/${doc.id}/revisions`);
      const formatted = (response.data || []).map((r: any) => ({
        ...r,
        createdAt: Array.isArray(r.createdAt) ?
          dayjs(new Date(r.createdAt[0], r.createdAt[1] - 1, r.createdAt[2], r.createdAt[3], r.createdAt[4], r.createdAt[5])).format('YYYY-MM-DD HH:mm:ss') :
          r.createdAt
      }));
      setDocumentRevisions(formatted);
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to fetch document revisions',
        severity: 'error'
      });
      setDocumentRevisions([]);
    } finally {
      setLoadingRevisions(false);
    }
  };

  const handleRestoreRevisionAsCopy = async (revisionId: number) => {
    if (!viewDocumentsUser || !viewRevisionsDocument) return;
    try {
      const response = await api.post(
        `/admin/users/${viewDocumentsUser.id}/documents/${viewRevisionsDocument.id}/revisions/${revisionId}/restore-as-copy`
      );
      const restored = response.data;
      setSnackbar({
        open: true,
        message: `Restored as new document: ${restored?.name || 'success'}`,
        severity: 'success'
      });
      // Refresh the documents list to include the restored copy.
      handleViewDocuments(viewDocumentsUser);
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to restore revision',
        severity: 'error'
      });
    }
  };

  const handleDeleteDocument = async () => {
    if (!deleteDocument || !viewDocumentsUser) return;
    
    try {
      await api.delete(`/admin/users/${viewDocumentsUser.id}/documents/${deleteDocument.name}`);
      setSnackbar({
        open: true,
        message: 'Document deleted successfully',
        severity: 'success'
      });
      // Refresh the documents list
      handleViewDocuments(viewDocumentsUser);
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to delete document',
        severity: 'error'
      });
    } finally {
      setDeleteDocument(null);
    }
  };

  const handleOpenDocumentActions = (event: React.MouseEvent<HTMLElement>, doc: UserDocument) => {
    setDocumentActionsAnchorEl(event.currentTarget);
    setDocumentActionsDoc(doc);
  };

  const handleCloseDocumentActions = () => {
    setDocumentActionsAnchorEl(null);
    setDocumentActionsDoc(null);
  };

  const getExpirationStatus = (expirationDate: string | null, accountNeverExpires: boolean) => {
    if (accountNeverExpires) {
      return { 
        text: 'Never Expires', 
        color: 'success',
        icon: <LockIcon fontSize="small" />
      };
    }
    
    if (!expirationDate) {
      return { 
        text: 'No Expiration', 
        color: 'success',
        icon: <LockIcon fontSize="small" />
      };
    }

    const expiration = dayjs(expirationDate);
    const now = dayjs();
    
    if (expiration.isBefore(now)) {
      return { 
        text: 'Expired', 
        color: 'error',
        icon: <ErrorIcon fontSize="small" />
      };
    }
    
    const daysUntilExpiration = expiration.diff(now, 'day');
    if (daysUntilExpiration <= 7) {
      const hoursUntilExpiration = expiration.diff(now, 'hour');
      if (hoursUntilExpiration < 24) {
        return { 
          text: `${hoursUntilExpiration} hours remaining`, 
          color: 'error',
          icon: <ErrorIcon fontSize="small" />
        };
      }
      return { 
        text: `${daysUntilExpiration} days remaining`, 
        color: 'warning',
        icon: <AccessTimeIcon fontSize="small" />
      };
    }
    
    return { 
      text: expiration.format('MMM D, YYYY'), 
      color: 'success',
      icon: <CheckCircleIcon fontSize="small" />
    };
  };

  const handleRepair = async (checkName: string, repairId: string) => {
    setRepairingId(repairId);
    try {
      await api.post('/admin/sanity-checks/repair', { checkName, repairId });
      await fetchSanityReport();
    } catch (err) {
      // Optionally show error
    } finally {
      setRepairingId(null);
    }
  };

  const handleRepairAll = async () => {
    if (!sanityReport?.results) return;
    setRepairingAll(true);
    try {
      const repairable = sanityReport.results.filter((r: any) => r.repairable);
      await Promise.all(
        repairable.map((r: any) =>
          api.post('/admin/sanity-checks/repair', { checkName: r.checkName, repairId: r.repairId })
        )
      );
      await fetchSanityReport();
    } catch (err) {
      // Optionally show error
    } finally {
      setRepairingAll(false);
    }
  };

  return (
    <Container maxWidth={false} sx={{ 
      mt: 4, 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <Paper sx={{ 
        p: 3, 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        minHeight: 0 // This is important for flex child to scroll properly
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">Admin Console</Typography>
          <Button 
            variant="contained" 
            onClick={() => setChangePasswordOpen(true)}
          >
            Change Password
      </Button>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        
        {/* Statistics Section */}
        <Box sx={{ mb: 4 }}>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              cursor: 'pointer',
              mb: 2
            }}
            onClick={() => toggleSection('statistics')}
          >
            <Typography variant="h5">Statistics</Typography>
            <IconButton>
              {expandedSections.statistics ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={expandedSections.statistics}>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PeopleIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6">Total Users</Typography>
                    </Box>
                    <Typography variant="h4">{stats?.totalUsers || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PersonIcon color="success" sx={{ mr: 1 }} />
                      <Typography variant="h6">Active Users</Typography>
                    </Box>
                    <Typography variant="h4">{stats?.activeUsers || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AccessTimeIcon color="error" sx={{ mr: 1 }} />
                      <Typography variant="h6">Expired Users</Typography>
                    </Box>
                    <Typography variant="h4">{stats?.expiredUsers || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AdminPanelSettingsIcon color="info" sx={{ mr: 1 }} />
                      <Typography variant="h6">Administrators</Typography>
                    </Box>
                    <Typography variant="h4">{stats?.adminUsers || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PersonIcon color="warning" sx={{ mr: 1 }} />
                      <Typography variant="h6">Online Users</Typography>
                    </Box>
                    <Typography variant="h4">{onlineUsers.length}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <StorageIcon color="secondary" sx={{ mr: 1 }} />
                      <Typography variant="h6">Redis Keys</Typography>
                    </Box>
                    <Typography variant="h4">{stats?.redisDbSize || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <MemoryIcon color={stats?.redisMemoryUsage && stats.redisMemoryUsage > 1024 * 1024 * 1024 ? "error" : "secondary"} sx={{ mr: 1 }} />
                      <Typography variant="h6">Redis Memory</Typography>
                    </Box>
                    <Typography variant="h4" color={stats?.redisMemoryUsage && stats.redisMemoryUsage > 1024 * 1024 * 1024 ? "error" : "text.primary"}>
                      {stats?.redisMemoryUsage ? formatBytes(stats.redisMemoryUsage) : '0 B'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <DescriptionIcon color="secondary" sx={{ mr: 1 }} />
                      <Typography variant="h6">Documents</Typography>
                    </Box>
                    <Typography variant="h4">{stats?.totalDocuments || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <StorageOutlinedIcon color="secondary" sx={{ mr: 1 }} />
                      <Typography variant="h6">Revisions</Typography>
                    </Box>
                    <Typography variant="h4">{stats?.totalRevisions || 0}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stats?.totalRevisionBytes ? formatBytes(stats.totalRevisionBytes) : '0 B'}
                    </Typography>
                    {stats?.postgresDatabaseBytes != null && stats.postgresDatabaseBytes > 0 && (() => {
                      const revPct = clampStoragePercent(stats.totalRevisionBytes ?? 0, stats.postgresDatabaseBytes);
                      return (
                        <Box sx={{ mt: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Revision payload vs PostgreSQL database
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={revPct}
                            color={revPct >= 85 ? 'error' : revPct >= 50 ? 'warning' : 'primary'}
                            sx={{ mt: 0.5, height: 6, borderRadius: 1 }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {formatBytes(stats.totalRevisionBytes ?? 0)} revision data · {formatBytes(stats.postgresDatabaseBytes)} database
                          </Typography>
                          {stats.postgresDocumentRevisionsRelationBytes != null &&
                            stats.postgresDocumentRevisionsRelationBytes > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Revisions table (incl. TOAST / indexes): {formatBytes(stats.postgresDocumentRevisionsRelationBytes)}
                            </Typography>
                          )}
                        </Box>
                      );
                    })()}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PermMediaOutlinedIcon color="secondary" sx={{ mr: 1 }} />
                      <Typography variant="h6">Content media</Typography>
                    </Box>
                    <Typography variant="h4">{stats?.totalMediaFiles ?? 0}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stats?.totalMediaBytes != null ? formatBytes(stats.totalMediaBytes) : '0 B'}
                    </Typography>
                    {(stats?.mediaStagingBytes != null || stats?.mediaByContentBytes != null) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Staging {formatBytes(stats.mediaStagingBytes ?? 0)} · Stored {formatBytes(stats.mediaByContentBytes ?? 0)}
                      </Typography>
                    )}
                    {stats?.mediaVolumeTotalBytes != null && stats.mediaVolumeTotalBytes > 0 && (() => {
                      const volUsed =
                        stats.mediaVolumeUsedBytes ??
                        Math.max(0, stats.mediaVolumeTotalBytes - (stats.mediaVolumeUsableBytes ?? 0));
                      const volPct = clampStoragePercent(volUsed, stats.mediaVolumeTotalBytes);
                      return (
                        <Box sx={{ mt: 1.5 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Disk volume (media directory)
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={volPct}
                            color={volPct >= 90 ? 'error' : volPct >= 75 ? 'warning' : 'primary'}
                            sx={{ mt: 0.5, height: 6, borderRadius: 1 }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {formatBytes(volUsed)} used of {formatBytes(stats.mediaVolumeTotalBytes)} · app media{' '}
                            {formatBytes(stats.totalMediaBytes ?? 0)}
                          </Typography>
                        </Box>
                      );
                    })()}
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ mt: 1.5 }}
                      onClick={() => setStagingPurgeDialogOpen(true)}
                      disabled={stagingPurgeLoading}
                    >
                      Purge stale staging…
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ height: 300 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Users by Provider</Typography>
                    <Box sx={{ height: 200, width: '100%' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={getProviderData()}
                            cx="50%"
                            cy="50%"
                            innerRadius={30}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                            nameKey="name"
                            startAngle={0}
                            endAngle={360}
                            paddingAngle={2}
                          >
                            {getProviderData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip 
                            formatter={(value, name) => [`${value} Users`, name]}
                            contentStyle={{ 
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              padding: '8px'
                            }}
                          />
                          <Legend
                            verticalAlign="bottom"
                            align="center"
                            formatter={(value) => `${value} Users`}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ height: 300 }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <AddIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">New Documents</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="h4">{stats?.newDocuments24h || 0}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Last 24 Hours
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h6">{stats?.newDocuments7d || 0}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Last 7 Days
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h6">{stats?.newDocuments30d || 0}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Last 30 Days
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ height: 300 }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <PersonAddIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">New Users</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="h4">{stats?.newUsers24h || 0}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Last 24 Hours
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h6">{stats?.newUsers7d || 0}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Last 7 Days
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h6">{stats?.newUsers30d || 0}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Last 30 Days
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ height: 300 }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <AccessTimeIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">Sessions</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="h4">{stats?.activeSessions || 0}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Active Sessions
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h6">{Math.round(stats?.averageSessionDuration || 0)}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Avg. Session Duration (minutes)
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Collapse>
        </Box>
        
        {/* System Health Dashboard */}
        <Box sx={{ mb: 4 }}>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              cursor: 'pointer',
              mb: 2
            }}
            onClick={() => toggleSection('systemHealth')}
          >
            <Typography variant="h5">System Health</Typography>
            <IconButton>
              {expandedSections.systemHealth ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={expandedSections.systemHealth}>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <MemoryIcon sx={{ mr: 1, color: systemHealth ? getHealthColor(systemHealth.cpuUsage, { warning: 60, error: 80 }) : 'primary' }} />
                      <Typography variant="h6">CPU Usage</Typography>
                    </Box>
                    <Typography variant="h4" sx={{ mb: 1, color: systemHealth ? getHealthColor(systemHealth.cpuUsage, { warning: 60, error: 80 }) : 'primary' }}>
                      {systemHealth ? `${Math.round(systemHealth.cpuUsage)}%` : 'N/A'}
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={systemHealth?.cpuUsage || 0} 
                      color={systemHealth ? getHealthColor(systemHealth.cpuUsage, { warning: 60, error: 80 }) as 'success' | 'warning' | 'error' : 'primary'}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <MemoryIcon sx={{ mr: 1, color: systemHealth ? getHealthColor(systemHealth.memoryUsage, { warning: 60, error: 80 }) : 'primary' }} />
                      <Typography variant="h6">Memory Usage</Typography>
                    </Box>
                    <Typography variant="h4" sx={{ mb: 1, color: systemHealth ? getHealthColor(systemHealth.memoryUsage, { warning: 60, error: 80 }) : 'primary' }}>
                      {systemHealth ? `${Math.round(systemHealth.memoryUsage)}%` : 'N/A'}
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={systemHealth?.memoryUsage || 0} 
                      color={systemHealth ? getHealthColor(systemHealth.memoryUsage, { warning: 60, error: 80 }) as 'success' | 'warning' | 'error' : 'primary'}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <MemoryIcon sx={{ mr: 1, color: systemHealth ? getHealthColor(systemHealth.jvmMemoryUsage, { warning: 60, error: 80 }) : 'primary' }} />
                      <Typography variant="h6">JVM Memory Usage</Typography>
                    </Box>
                    <Typography variant="h4" sx={{ mb: 1, color: systemHealth ? getHealthColor(systemHealth.jvmMemoryUsage, { warning: 60, error: 80 }) : 'primary' }}>
                      {systemHealth ? `${Math.round(systemHealth.jvmMemoryUsage)}%` : 'N/A'}
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={systemHealth?.jvmMemoryUsage || 0} 
                      color={systemHealth ? getHealthColor(systemHealth.jvmMemoryUsage, { warning: 60, error: 80 }) as 'success' | 'warning' | 'error' : 'primary'}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <MemoryIcon sx={{ mr: 1, color: systemHealth ? getHealthColor(systemHealth.threadCount, { warning: 50, error: 100 }) : 'primary' }} />
                      <Typography variant="h6">Thread Count</Typography>
                    </Box>
                    <Typography variant="h4" sx={{ mb: 1, color: systemHealth ? getHealthColor(systemHealth.threadCount, { warning: 50, error: 100 }) : 'primary' }}>
                      {systemHealth ? systemHealth.threadCount : 'N/A'}
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={systemHealth ? (systemHealth.threadCount / 100) * 100 : 0} 
                      color={systemHealth ? getHealthColor(systemHealth.threadCount, { warning: 50, error: 100 }) as 'success' | 'warning' | 'error' : 'primary'}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <StorageIcon sx={{ mr: 1, color: systemHealth ? getStatusColor(systemHealth.databaseStatus) : 'primary' }} />
                      <Typography variant="h6">Database Status</Typography>
                    </Box>
                    <Typography variant="h4">
                      {systemHealth?.databaseStatus ? (
                        <Chip 
                          label={systemHealth.databaseStatus.toUpperCase()}
                          color={
                            systemHealth.databaseStatus === 'healthy' ? 'success' :
                            systemHealth.databaseStatus === 'degraded' ? 'warning' : 'error'
                          }
                        />
                      ) : 'N/A'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <StorageIcon sx={{ mr: 1, color: systemHealth ? getStatusColor(systemHealth.redisStatus) : 'primary' }} />
                      <Typography variant="h6">Redis Status</Typography>
                    </Box>
                    <Typography variant="h4">
                      {systemHealth?.redisStatus ? (
                        <Chip 
                          label={systemHealth.redisStatus.toUpperCase()}
                          color={
                            systemHealth.redisStatus === 'healthy' ? 'success' :
                            systemHealth.redisStatus === 'degraded' ? 'warning' : 'error'
                          }
                        />
                      ) : 'N/A'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <StorageIcon sx={{ mr: 1, color: systemHealth ? getStatusColor(systemHealth.embeddingStatus) : 'primary' }} />
                      <Typography variant="h6" noWrap>Embedding Service</Typography>
                    </Box>
                    <Typography variant="h4">
                      {systemHealth?.embeddingStatus ? (
                        <Chip 
                          label={systemHealth.embeddingStatus.toUpperCase()}
                          color={
                            systemHealth.embeddingStatus === 'healthy' ? 'success' :
                            systemHealth.embeddingStatus === 'degraded' ? 'warning' : 'error'
                          }
                        />
                      ) : 'N/A'}
                    </Typography>
                    {systemHealth?.embeddingError && (
                      <Typography color="error" variant="body2">
                        Error: {systemHealth.embeddingError}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <SpeedIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6">API Response Time</Typography>
                    </Box>
                    <Typography variant="h4">
                      {systemHealth?.apiResponseTime ? `${systemHealth.apiResponseTime.toFixed(0)}ms` : 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Average response time
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Collapse>
        </Box>
        
        {/* User Management Section */}
        <Box sx={{ mb: 4 }}>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              cursor: 'pointer',
              mb: 2
            }}
            onClick={() => toggleSection('userManagement')}
          >
            <Typography variant="h5">User Management</Typography>
            <IconButton>
              {expandedSections.userManagement ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={expandedSections.userManagement}>
            <Box sx={{ mb: 2 }}>
              <TextField
                variant="outlined"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
                sx={{ width: '300px' }}
              />
            </Box>
            
            {/* Admins Table */}
            <Typography variant="h6" sx={{ mb: 2 }}>Administrators</Typography>
            <TableContainer sx={{ maxHeight: 300, mb: 4 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{renderSortLabel('username', 'Username')}</TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>{renderSortLabel('creationDate', 'Created')}</TableCell>
                    <TableCell>{renderSortLabel('expirationDate', 'Expiration')}</TableCell>
                    <TableCell>{renderSortLabel('lastLoginAt', 'Last Login')}</TableCell>
                    <TableCell>Online</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getSortedUsers(filterUsers(users.filter(user => user.admin), searchTerm)).map(user => (
                    <TableRow key={user.id}>
                      <TableCell>{user.username}</TableCell>
                      <TableCell>{user.provider}</TableCell>
                      <TableCell>
                        {dayjs(user.creationDate).format('YYYY-MM-DD HH:mm')}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          icon={getExpirationStatus(user.expirationDate, user.accountNeverExpires).icon}
                          label={getExpirationStatus(user.expirationDate, user.accountNeverExpires).text}
                          color={getExpirationStatus(user.expirationDate, user.accountNeverExpires).color as 'success' | 'warning' | 'error'}
                          sx={{ 
                            '& .MuiChip-icon': { 
                              color: 'inherit' 
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {user.lastLoginAt ? 
                          dayjs(user.lastLoginAt).format('YYYY-MM-DD HH:mm') : 
                          'Never'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ 
                          display: 'inline-block',
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: isUserOnline(user.username) ? 'success.main' : 'grey.500',
                          mr: 1
                        }} />
                        {isUserOnline(user.username) ? 'Online' : 'Offline'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton onClick={(e) => handleMenuOpen(e, user)}>
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Regular Users Table */}
            <Typography variant="h6" sx={{ mb: 2 }}>Users</Typography>
            <TableContainer sx={{ maxHeight: 400, mb: 4 }}>
              <Table stickyHeader>
            <TableHead>
              <TableRow>
                    <TableCell>{renderSortLabel('username', 'Username')}</TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>{renderSortLabel('creationDate', 'Created')}</TableCell>
                    <TableCell>{renderSortLabel('expirationDate', 'Expiration')}</TableCell>
                    <TableCell>{renderSortLabel('lastLoginAt', 'Last Login')}</TableCell>
                    <TableCell>Online</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
                  {getSortedUsers(filterUsers(users.filter(user => !user.admin), searchTerm)).map(user => (
                <TableRow key={user.id}>
                  <TableCell>{user.username}</TableCell>
                      <TableCell>{user.provider}</TableCell>
                      <TableCell>
                        {dayjs(user.creationDate).format('YYYY-MM-DD HH:mm')}
                      </TableCell>
                  <TableCell>
                        <Chip
                          size="small"
                          icon={getExpirationStatus(user.expirationDate, user.accountNeverExpires).icon}
                          label={getExpirationStatus(user.expirationDate, user.accountNeverExpires).text}
                          color={getExpirationStatus(user.expirationDate, user.accountNeverExpires).color as 'success' | 'warning' | 'error'}
                          sx={{ 
                            '& .MuiChip-icon': { 
                              color: 'inherit' 
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {user.lastLoginAt ? 
                          dayjs(user.lastLoginAt).format('YYYY-MM-DD HH:mm') : 
                          'Never'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ 
                          display: 'inline-block',
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: isUserOnline(user.username) ? 'success.main' : 'grey.500',
                          mr: 1
                        }} />
                        {isUserOnline(user.username) ? 'Online' : 'Offline'}
                  </TableCell>
                  <TableCell align="right">
                        <IconButton onClick={(e) => handleMenuOpen(e, user)}>
                          <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

            {/* Online Users Table */}
            <Typography variant="h6" sx={{ mb: 2 }}>Online Users</Typography>
            <TableContainer sx={{ maxHeight: 300 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>Login Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {onlineUsers.map(user => (
                    <TableRow key={user.id}>
                      <TableCell>{user.username}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.provider}</TableCell>
                      <TableCell>
                        {dayjs(user.loginTime).format('YYYY-MM-DD HH:mm:ss')}
                      </TableCell>
                    </TableRow>
                  ))}
                  {onlineUsers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No users currently online
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </Box>

        {/* Open Documents Section */}
        <Box sx={{ mb: 4 }}>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              cursor: 'pointer',
              mb: 2
            }}
            onClick={() => toggleSection('openDocuments')}
          >
            <Typography variant="h5">Open Documents</Typography>
            <IconButton>
              {expandedSections.openDocuments ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={expandedSections.openDocuments}>
            <Box sx={{ mb: 2 }}>
              <TextField
                variant="outlined"
                placeholder="Search by document name, user, email, or id…"
                value={openDocumentsSearch}
                onChange={e => setOpenDocumentsSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
                sx={{ width: { xs: '100%', sm: 400 } }}
              />
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Document Name</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Last Updated</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOpenDocuments.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell>{doc.name}</TableCell>
                      <TableCell>{doc.user.username}</TableCell>
                      <TableCell>
                        {doc.creationDate ? 
                          dayjs(doc.creationDate).format('MMM D, YYYY HH:mm:ss') : 
                          'N/A'
                        }
                      </TableCell>
                      <TableCell>
                        {doc.updatedAt ? 
                          dayjs(doc.updatedAt).format('MMM D, YYYY HH:mm:ss') : 
                          'N/A'
                        }
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                          <Button
                            variant="outlined"
                            color="success"
                            size="small"
                            onClick={() => setSaveToDbDoc(doc)}
                          >
                            Save to DB
                          </Button>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            onClick={() => setReloadCacheDoc(doc)}
                          >
                            Reload cache
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => handleCloseDocument(String(doc.id))}
                          >
                            Close
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {openDocuments.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No open documents
                      </TableCell>
                    </TableRow>
                  )}
                  {openDocuments.length > 0 && filteredOpenDocuments.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No documents match your search
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </Box>

        {/* Audit Logs Section */}
        <Box sx={{ mb: 4 }}>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              cursor: 'pointer',
              mb: 2
            }}
            onClick={() => toggleSection('auditLogs')}
          >
            <Typography variant="h5">Audit Logs</Typography>
            <IconButton>
              {expandedSections.auditLogs ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={expandedSections.auditLogs}>
            <AuditLogsTable />
          </Collapse>
        </Box>

        {/* Sanity Checks Section */}
        <Box sx={{ mb: 4 }}>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              cursor: 'pointer',
              mb: 2
            }}
            onClick={() => toggleSection('sanityChecks')}
          >
            <Typography variant="h5">
              Sanity Checks
              {sanityReport?.results && sanityReport.results.length > 0 && (
                <span style={{ color: '#d32f2f', fontWeight: 500, marginLeft: 8, fontSize: 18 }}>
                  ({sanityReport.results.length})
                </span>
              )}
            </Typography>
            <IconButton>
              {expandedSections.sanityChecks ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={expandedSections.sanityChecks}>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button variant="contained" onClick={runSanityChecks} disabled={loadingSanity}>
                {loadingSanity ? <CircularProgress size={20} /> : 'Run Sanity Checks'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={handleRepairAll}
                disabled={repairingAll || !sanityReport?.results?.some((r: any) => r.repairable)}
                startIcon={repairingAll ? <CircularProgress size={16} /> : null}
              >
                Repair All
              </Button>
              {sanityReport?.runAt && (
                <Typography variant="body2" color="text.secondary">
                  Last Run: {new Date(sanityReport.runAt).toLocaleString()}
                </Typography>
              )}
            </Box>
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Check</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sanityReport?.results?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No issues found (or no checks implemented yet).
                      </TableCell>
                    </TableRow>
                  )}
                  {sanityReport?.results?.map((result: any, idx: number) => (
                    <TableRow key={idx}>
                      <TableCell>{result.checkName}</TableCell>
                      <TableCell>
                        {result.status === 'OK' && <CheckCircleIcon sx={{ color: 'green', verticalAlign: 'middle', mr: 1 }} />}
                        {result.status === 'WARNING' && <WarningIcon sx={{ color: 'orange', verticalAlign: 'middle', mr: 1 }} />}
                        {result.status === 'ERROR' && <ErrorIcon sx={{ color: 'red', verticalAlign: 'middle', mr: 1 }} />}
                        {result.status}
                      </TableCell>
                      <TableCell>{result.details}</TableCell>
                      <TableCell>
                        {result.repairable && (
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleRepair(result.checkName, result.repairId)}
                            disabled={repairingId === result.repairId}
                            startIcon={repairingId === result.repairId ? <CircularProgress size={16} /> : null}
                          >
                            Repair
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </Box>

        {/* Actions Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => handleAction('view-details')}>
            View Details
          </MenuItem>
          <MenuItem onClick={() => handleAction('view-documents')}>
            View Documents
          </MenuItem>
          {!selectedUser?.admin && (
            <MenuItem onClick={() => handleAction('make-admin')}>
              Make Admin
            </MenuItem>
          )}
          {selectedUser?.admin && !isCurrentUser(selectedUser) && (
            <MenuItem onClick={() => handleAction('remove-admin')}>
              Remove Admin
            </MenuItem>
          )}
          <MenuItem 
            onClick={() => handleAction('expiration')}
            disabled={Boolean(selectedUser && isCurrentUser(selectedUser))}
          >
            Set Expiration
          </MenuItem>
          {!selectedUser?.accountNeverExpires && (
            <MenuItem 
              onClick={() => handleAction('set-never-expire')}
              disabled={Boolean(selectedUser && isCurrentUser(selectedUser))}
            >
              Set Never Expire
            </MenuItem>
          )}
          {selectedUser?.provider === 'LOCAL' && (
            <MenuItem 
              onClick={() => handleAction('reset')}
              disabled={Boolean(selectedUser && isCurrentUser(selectedUser))}
            >
              Reset Password
            </MenuItem>
          )}
          <MenuItem 
            onClick={() => handleAction('delete')} 
            disabled={Boolean(selectedUser && isCurrentUser(selectedUser))}
            sx={{ color: 'error.main' }}
          >
            Delete User
          </MenuItem>
        </Menu>

        {/* Set Expiration Dialog */}
        <Dialog open={!!editUser} onClose={() => setEditUser(null)}>
          <DialogTitle>Set Expiration for {editUser?.username}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Enter the new expiration date and time (leave empty for no expiration):
            </DialogContentText>
            <TextField
              type="datetime-local"
              fullWidth
              value={newExpiration}
              onChange={e => setNewExpiration(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditUser(null)}>Cancel</Button>
            <Button onClick={handleSetExpiration} variant="contained">Save</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!saveToDbDoc} onClose={() => !saveToDbLoading && setSaveToDbDoc(null)}>
          <DialogTitle>Save document to database?</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Writes the current server cache (Redis) into the document blob in the database. If there are unpersisted
              edits, the previous blob is stored as a revision checkpoint first—the same behavior as the periodic save
              task.
            </Alert>
            <DialogContentText>
              Document: <strong>{saveToDbDoc?.name}</strong> (user {saveToDbDoc?.user.username})
            </DialogContentText>
            <DialogContentText sx={{ mt: 2, color: 'text.secondary' }}>
              If nothing changed since the last save, no write occurs and you will see an “already matches” message.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSaveToDbDoc(null)} disabled={saveToDbLoading}>
              Cancel
            </Button>
            <Button
              onClick={confirmPersistOpenDocumentToDatabase}
              color="success"
              variant="contained"
              disabled={saveToDbLoading}
            >
              {saveToDbLoading ? 'Saving…' : 'Save to database'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!reloadCacheDoc} onClose={() => !reloadCacheLoading && setReloadCacheDoc(null)}>
          <DialogTitle>Reload document cache from database?</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This replaces the server Redis cache for this document with the last data saved in the database.
              Any changes present only in the cache (not yet persisted) will be lost.
            </Alert>
            <DialogContentText>
              Document: <strong>{reloadCacheDoc?.name}</strong> (user {reloadCacheDoc?.user.username})
            </DialogContentText>
            <DialogContentText sx={{ mt: 2, color: 'text.secondary' }}>
              Users who have this document open may need to refresh or reopen it to see the updated tree.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReloadCacheDoc(null)} disabled={reloadCacheLoading}>
              Cancel
            </Button>
            <Button
              onClick={confirmReloadOpenDocumentCache}
              color="primary"
              variant="contained"
              disabled={reloadCacheLoading}
            >
              {reloadCacheLoading ? 'Reloading…' : 'Reload cache'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete User Dialog */}
        <Dialog open={!!deleteUser} onClose={() => setDeleteUser(null)}>
          <DialogTitle sx={{ color: 'error.main' }}>Delete User</DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              Warning: This action cannot be undone.
            </Alert>
            <DialogContentText>
              Are you sure you want to delete user <strong>{deleteUser?.username}</strong>?
            </DialogContentText>
            <DialogContentText sx={{ mt: 2, color: 'text.secondary' }}>
              This will permanently remove the user's account and all associated data from the system.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteUser(null)}>Cancel</Button>
            <Button 
              onClick={handleDeleteUser} 
              color="error" 
              variant="contained"
              sx={{ 
                '&:hover': {
                  backgroundColor: 'error.dark'
                }
              }}
            >
              Delete User
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={!!resetPasswordUser} onClose={() => setResetPasswordUser(null)}>
          <DialogTitle>Reset Password</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to send a password reset email to {resetPasswordUser?.username}?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetPasswordUser(null)}>Cancel</Button>
            <Button onClick={handleResetPassword} color="primary" variant="contained">Send Reset Email</Button>
          </DialogActions>
        </Dialog>

        {/* Purge content media staging */}
        <Dialog
          open={stagingPurgeDialogOpen}
          onClose={() => !stagingPurgeLoading && setStagingPurgeDialogOpen(false)}
        >
          <DialogTitle>Purge stale staging uploads?</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Remove unfinished uploads under the staging folder when both the data file and sidecar (if any) are older
              than the age below. Saved attachments under stored content paths are not removed.
              {stats?.mediaStagingPurgeEnabled === false && (
                <span> The daily scheduled purge is disabled in configuration; this still runs on demand.</span>
              )}
            </DialogContentText>
            <TextField
              label="Minimum age (days)"
              type="number"
              fullWidth
              size="small"
              inputProps={{ min: 1, max: 3650, step: 1 }}
              value={stagingPurgeRetentionDaysInput}
              onChange={(e) => setStagingPurgeRetentionDaysInput(e.target.value)}
              disabled={stagingPurgeLoading}
              helperText="Files with last-modified newer than this many days are kept. Defaults from server config when you open this dialog; change it to purge more aggressively (e.g. 1)."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStagingPurgeDialogOpen(false)} disabled={stagingPurgeLoading}>
              Cancel
            </Button>
            <Button onClick={confirmStagingPurge} variant="contained" disabled={stagingPurgeLoading}>
              {stagingPurgeLoading ? 'Purging…' : 'Purge now'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)}>
          <DialogTitle>Change Password</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Please enter your current password and choose a new password.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Current Password"
              type="password"
              fullWidth
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="New Password"
              type="password"
              fullWidth
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setChangePasswordOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleChangePassword} 
              variant="contained"
              disabled={!currentPassword || !newPassword}
            >
              Change Password
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Details Dialog */}
        <Dialog 
          open={!!viewDetailsUser || loadingDetails || !!detailsError} 
          onClose={() => {
            setViewDetailsUser(null);
            setDetailsError(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>User Details</DialogTitle>
          <DialogContent>
            {loadingDetails ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : detailsError ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {detailsError}
              </Alert>
            ) : viewDetailsUser && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Username</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{viewDetailsUser.username}</Typography>

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Email</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{viewDetailsUser.email}</Typography>

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Name</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {viewDetailsUser.firstName} {viewDetailsUser.lastName}
                </Typography>

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Account Status</Typography>
                <Typography variant="body1" color={viewDetailsUser.accountNeverExpires || (viewDetailsUser.expirationDate && dayjs(viewDetailsUser.expirationDate).isAfter(dayjs())) ? 'success.main' : 'error.main'} sx={{ mb: 2 }}>
                  {viewDetailsUser.accountNeverExpires || (viewDetailsUser.expirationDate && dayjs(viewDetailsUser.expirationDate).isAfter(dayjs())) ? 'Active' : 'Expired'}
                </Typography>

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Expiration</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {viewDetailsUser.accountNeverExpires ? 'Never' : (
                    viewDetailsUser.expirationDate ? 
                      dayjs(viewDetailsUser.expirationDate).format('YYYY-MM-DD HH:mm') : 
                      'Never'
                  )}
                </Typography>

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Created</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {viewDetailsUser.creationDate ? 
                    dayjs(viewDetailsUser.creationDate).format('YYYY-MM-DD HH:mm:ss') : 
                    'N/A'}
                </Typography>

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Provider</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {viewDetailsUser.provider || 'N/A'}
                </Typography>

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Last Login</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {viewDetailsUser.lastLoginAt ? 
                    dayjs(viewDetailsUser.lastLoginAt).format('YYYY-MM-DD HH:mm:ss') : 
                    'Never'}
                </Typography>

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Documents</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {viewDetailsUser.documentCount || 0} documents
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setViewDetailsUser(null);
              setDetailsError(null);
            }}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* View Documents Dialog */}
        <Dialog 
          open={!!viewDocumentsUser} 
          onClose={() => {
            setViewDocumentsUser(null);
            setUserDocuments([]);
            setViewRevisionsDocument(null);
            setDocumentRevisions([]);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Documents for {viewDocumentsUser?.username}
          </DialogTitle>
          <DialogContent>
            {loadingDocuments ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Document Name</TableCell>
                      <TableCell>Revisions</TableCell>
                      <TableCell>Latest Revision</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Last Updated</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userDocuments.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell>{doc.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={doc.revisionCount ?? 0}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {doc.latestRevisionAt ?
                            dayjs(doc.latestRevisionAt).format('MMM D, YYYY HH:mm:ss') :
                            '—'
                          }
                        </TableCell>
                        <TableCell>
                          {doc.creationDate ? 
                            dayjs(doc.creationDate).format('MMM D, YYYY HH:mm:ss') : 
                            'N/A'
                          }
                        </TableCell>
                        <TableCell>
                          {doc.updatedAt ? 
                            dayjs(doc.updatedAt).format('MMM D, YYYY HH:mm:ss') : 
                            'N/A'
                          }
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenDocumentActions(e, doc)}
                            aria-label="document actions"
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {userDocuments.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No documents found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setViewDocumentsUser(null);
              setUserDocuments([]);
              setViewRevisionsDocument(null);
              setDocumentRevisions([]);
            }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Document actions menu */}
        <Menu
          anchorEl={documentActionsAnchorEl}
          open={Boolean(documentActionsAnchorEl)}
          onClose={handleCloseDocumentActions}
        >
          <MenuItem
            onClick={() => {
              const doc = documentActionsDoc;
              handleCloseDocumentActions();
              if (!doc) return;
              handleViewRevisions(doc);
            }}
            disabled={!documentActionsDoc || (documentActionsDoc.revisionCount ?? 0) === 0}
          >
            Show revisions
          </MenuItem>
          <MenuItem
            onClick={() => {
              const doc = documentActionsDoc;
              handleCloseDocumentActions();
              if (!doc) return;
              setDeleteDocument(doc);
            }}
            sx={{ color: 'error.main' }}
          >
            Delete
          </MenuItem>
        </Menu>

        {/* View Revisions Dialog */}
        <Dialog
          open={!!viewRevisionsDocument}
          onClose={() => {
            setViewRevisionsDocument(null);
            setDocumentRevisions([]);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Revisions for {viewRevisionsDocument?.name}
          </DialogTitle>
          <DialogContent>
            {loadingRevisions ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Created</TableCell>
                      <TableCell>Doc Rev</TableCell>
                      <TableCell>Size</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {documentRevisions.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>
                          {r.createdAt ? dayjs(r.createdAt).format('MMM D, YYYY HH:mm:ss') : 'N/A'}
                        </TableCell>
                        <TableCell>{r.documentRevision}</TableCell>
                        <TableCell>{formatBytes(r.sizeBytes || 0)}</TableCell>
                        <TableCell align="right">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleRestoreRevisionAsCopy(r.id)}
                          >
                            Restore as copy
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {documentRevisions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No revisions found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setViewRevisionsDocument(null);
              setDocumentRevisions([]);
            }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Document Confirmation Dialog */}
        <Dialog 
          open={!!deleteDocument} 
          onClose={() => setDeleteDocument(null)}
        >
          <DialogTitle sx={{ color: 'error.main' }}>Delete Document</DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              Warning: This action cannot be undone.
            </Alert>
            <DialogContentText>
              Are you sure you want to delete document <strong>{deleteDocument?.name}</strong>?
            </DialogContentText>
            <DialogContentText sx={{ mt: 2, color: 'text.secondary' }}>
              This will permanently remove the document and all associated data from the system.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDocument(null)}>Cancel</Button>
            <Button 
              onClick={handleDeleteDocument} 
              color="error" 
              variant="contained"
              sx={{ 
                '&:hover': {
                  backgroundColor: 'error.dark'
                }
              }}
            >
              Delete Document
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
  );
};

export default AdminConsolePage; 